import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "./ui/dialog";
import { ArrowRight, MapPin, Calendar, Maximize2 } from "lucide-react";
import { useScrollReveal } from "../hooks/use-scroll-reveal";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

// Fallback projects data
const fallbackProjects = [
  {
    id: "1",
    title: "Modern Living Room Transformation",
    category: "interior-painting",
    location: "Winter Park, FL",
    year: "2024",
    description: "This project featured a refined interior refresh for an open-concept living and dining area, transitioning the space from a plain white finish to a sophisticated, warm agreeable gray. Our painting team performed meticulous surface prep and precise application across the walls and architectural surfaces. This update introduces subtle warmth and elegance, creating a modern, cozy atmosphere that elevates the entire living space.",
    before_image: "/images/living-room-before.jpg",
    after_image: "/images/living-room-after.jpg",
    before_alt: "Living room before interior house painters in Orlando Florida began work",
    after_alt: "Interior painters near Orlando Florida completed modern living room transformation",
  },
  {
    id: "2",
    title: "Coastal Home Exterior",
    category: "exterior-painting",
    location: "Orlando, FL",
    year: "2024",
    description: "This project involved a complete exterior color transformation to modernize the home's aesthetic while maintaining its traditional charm. We updated the main stucco body with a light, warm greige, providing a clean and inviting backdrop. To create crisp definition, all trim and architectural elements were painted in a bright, clean white. This high-contrast scheme refreshes the look of the home, giving it a sophisticated, transitional feel that complements the existing landscaping and dark roof.",
    before_image: "/images/coastal-home-before.jpg",
    after_image: "/images/coastal-home-after.jpg",
    before_alt: "Home exterior before exterior painters in Orlando Florida started project",
    after_alt: "Exterior painters in Orlando Florida completed weather resistant home finish",
  },
  {
    id: "3",
    title: "Popcorn Ceiling Removal",
    category: "popcorn-ceiling",
    location: "Sanford, FL",
    year: "2024",
    description: "Removed dated popcorn texture and applied smooth finish.",
    before_image: "https://images.pexels.com/photos/3615725/pexels-photo-3615725.jpeg?auto=compress&cs=tinysrgb&w=800",
    after_image: "https://images.unsplash.com/photo-1649083048269-8bfb755e7b87?w=800",
    before_alt: "Popcorn ceiling before commercial painting companies in Seminole County Florida removal",
    after_alt: "Commercial painting companies in Seminole County Florida smooth ceiling finish",
  },
  {
    id: "4",
    title: "Casselberry Home Exterior",
    category: "exterior-painting",
    location: "Casselberry, FL",
    year: "2025",
    description: "The main body of the home, previously white, was repainted in a soft, lite gray. This new gray color provides a sophisticated backdrop that contrasts beautifully with the existing bright white trims, window frames, and porch columns, creating a clean, modern, and well-defined look. The dark roof and front door remain, grounding the design.",
    before_image: "/images/casselberry-exterior-before.jpg",
    after_image: "/images/casselberry-exterior-after.jpg",
    before_alt: "Home exterior before painting services in Casselberry Florida began work",
    after_alt: "Painting services in Casselberry Florida completed modern exterior transformation",
  },
  {
    id: "5",
    title: "Commercial Exterior Painting",
    category: "commercial",
    location: "Longwood, FL",
    year: "2025",
    description: "This exterior painting companies in Longwood Florida project features a bold blue stripe that transformed the building's facade. The vibrant branding element demonstrates our team's precision and high-quality craftsmanship on commercial projects.",
    before_image: "/images/commercial-exterior-before.jpg",
    after_image: "/images/commercial-exterior-after.jpg",
    before_alt: "Commercial building before exterior painting companies in Longwood Florida began work",
    after_alt: "Exterior painting companies in Longwood Florida completed bold blue stripe branding",
  },
  {
    id: "6",
    title: "Contemporary Home Repaint",
    category: "exterior-painting",
    location: "Altamonte Springs, FL",
    year: "2024",
    description: "This home's exterior was revitalized from a traditional stucco design into a modern, transitional classic. The primary stucco body was brightened with a clean, crisp white, creating a fresh, inviting canvas. This modern white contrasts sharply with the rich, dark, almost charcoal trim applied to all fascia, soffits, and window frames. This high-contrast color scheme emphasizes the home's clean lines and architectural gables. Further elevating the design is the addition of warm wood-look soffits beneath the front porch ceiling, introducing natural texture that complements the overall contemporary aesthetic. The result is a sophisticated update that is both timeless and striking.",
    before_image: "/images/contemporary-home-before.jpg",
    after_image: "/images/contemporary-home-after.jpg",
    before_alt: "Home before exterior painting companies in Longwood Florida repaint",
    after_alt: "Exterior painting companies in Longwood Florida modern home finish",
  },
  {
    id: "7",
    title: "Open Concept Living Space",
    category: "interior-painting",
    location: "Oviedo, FL",
    year: "2024",
    description: "Our interior house painters in Orlando Florida transformed this open-concept living and dining space from stark white to a warm, creamy beige. The soft neutral tone adds depth and warmth while allowing white shutters, doors, and ceilings to pop with crisp definition. A perfect example of how interior painters near Orlando Florida create inviting atmospheres.",
    before_image: "/images/open-concept-before.jpg",
    after_image: "/images/open-concept-after.jpg",
    before_alt: "Open concept living space before interior house painters in Orlando Florida began work",
    after_alt: "Interior painters near Orlando Florida completed warm beige transformation",
  },
  {
    id: "8",
    title: "Commercial Interior Painting Project",
    category: "commercial",
    location: "Orlando, FL",
    year: "2023",
    description: "Our commercial painting companies in Seminole County Florida transformed this conference room from cool blue to a sophisticated terracotta. The rich, earthy tone contrasts beautifully with dark wood furniture while crisp white trim maintains a professional, balanced aesthetic.",
    before_image: "/images/commercial-before.jpg",
    after_image: "/images/commercial-after.jpg",
    before_alt: "Commercial conference room before commercial painting companies in Seminole County Florida began work",
    after_alt: "Commercial painting companies in Seminole County Florida completed professional boardroom transformation",
  },
];

const ProjectCard = ({ project, onSelect, className = "" }) => (
  <Dialog>
    <DialogTrigger asChild>
      <div
        data-testid={`project-card-${project.id}`}
        className={`group relative cursor-pointer overflow-hidden rounded-lg border border-border/40 hover:border-brand-orange/40 card-lift ${className}`}
        onClick={() => onSelect(project)}
      >
        <img
          src={project.after_image}
          alt={project.after_alt || `${project.title} - painting project completed in ${project.location}`}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-brand-navy/20 to-transparent" />

        {/* Expand icon */}
        <div className="absolute top-3 right-3 w-8 h-8 bg-white/10 backdrop-blur-sm rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Maximize2 className="w-3.5 h-3.5 text-white" />
        </div>

        {/* Category badge */}
        <Badge className="absolute top-3 left-3 bg-brand-orange text-white font-heading uppercase text-[10px] tracking-wider px-2 py-0.5 font-semibold">
          {project.category}
        </Badge>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-heading text-base sm:text-lg font-semibold uppercase text-white leading-tight">
            {project.title}
          </h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-white/60">
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {project.location}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {project.year}
            </span>
          </div>
        </div>
      </div>
    </DialogTrigger>

    <DialogContent className="max-w-4xl p-0 overflow-hidden rounded-lg max-h-[90vh] overflow-y-auto">
      <DialogTitle className="sr-only">{project.title} - Before and After</DialogTitle>
      <div className="grid md:grid-cols-2">
        <div className="relative">
          <img
            src={project.before_image}
            alt={project.before_alt || `${project.title} - before painting in ${project.location}`}
            className="w-full h-40 sm:h-56 md:h-full object-cover"
          />
          <Badge className="absolute top-4 left-4 bg-brand-slate text-white font-heading uppercase">
            Before
          </Badge>
        </div>
        <div className="relative">
          <img
            src={project.after_image}
            alt={project.after_alt || `${project.title} - after painting completed in ${project.location}`}
            className="w-full h-40 sm:h-56 md:h-full object-cover"
          />
          <Badge className="absolute top-4 left-4 bg-brand-orange text-white font-heading uppercase">
            After
          </Badge>
        </div>
      </div>
      <div className="p-4 sm:p-5 md:p-6 bg-white">
        <Badge className="bg-brand-ice text-brand-navy font-heading uppercase text-xs mb-3">
          {project.category}
        </Badge>
        <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold uppercase text-brand-navy">
          {project.title}
        </h3>
        <div className="flex items-center gap-4 mt-2 text-sm text-brand-slate">
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {project.location}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {project.year}
          </span>
        </div>
        <p className="text-brand-slate mt-3 sm:mt-4 text-sm sm:text-base leading-relaxed">{project.description}</p>
        <Link to={`/quote?project=${project.id}`}>
          <Button 
            data-testid="start-similar-project-btn"
            className="mt-4 sm:mt-5 w-full sm:w-auto bg-brand-orange text-white font-heading font-semibold uppercase hover:bg-[#d9432a] transition-colors duration-300"
          >
            Start Similar Project
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
    </DialogContent>
  </Dialog>
);

export const Portfolio = ({ showAll = false, limit = 4 }) => {
  const [projects, setProjects] = useState(fallbackProjects);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [gridRef, gridVisible] = useScrollReveal();

  useEffect(() => {
    const fetchProjects = async () => {
      if (!API) return; // No backend, use fallback data already set
      try {
        const response = await axios.get(`${API}/projects`);
        if (response.data.projects?.length) {
          setProjects(response.data.projects);
        }
      } catch (error) {
        // Keep using fallback data
        console.log("Using fallback projects data");
      }
    };
    fetchProjects();
  }, []);

  const categories = [
    { id: "all", label: "All" },
    { id: "interior-painting", label: "Interior" },
    { id: "exterior-painting", label: "Exterior" },
    { id: "commercial", label: "Commercial" },
    { id: "popcorn-ceiling", label: "Ceilings" },
    { id: "deck-staining", label: "Decks" },
  ];

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter((p) => p.category === filter);

  // For "all" filter with a limit, pick a diverse mix across categories
  const getMixedProjects = () => {
    if (filter !== "all" || showAll) return filteredProjects;
    const cats = ["interior-painting", "exterior-painting", "popcorn-ceiling", "garage-floor", "deck-staining"];
    const mixed = [];
    let idx = 0;
    while (mixed.length < limit && idx < projects.length) {
      for (const cat of cats) {
        const next = projects.find(
          (p) => p.category === cat && !mixed.includes(p)
        );
        if (next && mixed.length < limit) mixed.push(next);
      }
      idx += cats.length;
    }
    return mixed;
  };

  const getDisplayedProjects = () => {
    if (showAll) return filteredProjects;
    if (filter === "all") return getMixedProjects().slice(0, limit);
    return filteredProjects.slice(0, limit);
  };
  const displayedProjects = getDisplayedProjects();

  return (
    <section
      data-testid="portfolio-section"
      className="py-20 sm:py-28 bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-3">
            Our Work
          </span>
          <h2
            data-testid="portfolio-heading"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-brand-navy"
          >
            Project Portfolio
          </h2>
          <p className="font-body text-sm sm:text-base text-brand-slate mt-3 max-w-xl mx-auto">
            Explore our completed projects showcasing quality craftsmanship
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center mb-10 sm:mb-12">
          <Tabs value={filter} onValueChange={setFilter} className="w-full max-w-2xl">
            <TabsList className="grid grid-cols-3 sm:grid-cols-6 bg-brand-ice p-1 rounded-lg h-auto gap-1">
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  data-testid={`filter-${cat.id}`}
                  className="font-heading text-[10px] sm:text-xs uppercase tracking-wide data-[state=active]:bg-brand-navy data-[state=active]:text-white rounded-md py-2 px-2"
                >
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Uniform 4-column Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 reveal-stagger ${gridVisible ? 'visible' : ''}`}
        >
          {displayedProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onSelect={setSelectedProject}
              className="aspect-[3/4] sm:aspect-[4/5]"
            />
          ))}
        </div>

        {/* View All CTA */}
        {!showAll && projects.length > limit && (
          <div className="text-center mt-12 sm:mt-14">
            <Link to="/portfolio">
              <Button
                data-testid="view-all-portfolio-btn"
                variant="outline"
                size="lg"
                className="border-2 border-brand-navy text-brand-navy font-heading font-semibold uppercase tracking-wide hover:bg-brand-navy hover:text-white transition-all duration-300"
              >
                View All Projects
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
